/**
 * Remediation Impact Calculator
 *
 * Lives under the MCL legend (rendered by ThreePlumeVisualization in
 * bars-only mode). Shows active remediation systems for the site, with a
 * per-system PSH/dissolved-mass-removed preview at each v3.2 horizon, and a
 * "what-if" draft mode for adding a system before persisting.
 *
 * Two-way sync: reads + writes the same `remediation_systems` table the
 * CostAnalysisTab's RemediationSystemsPanel uses, so edits in one panel
 * propagate to the other.
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { PlusIcon, TrashIcon, XMarkIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import {
  fetchRemediationSystems,
  fetchRemediationImpact,
  createRemediationSystem,
  deleteRemediationSystem,
  type RemediationSystemItem,
  type RemediationImpactResponse,
  type RemediationDraftSystem,
} from '../../api/polygon';

const TECHNOLOGIES = [
  { value: 'mdpe', label: 'MDPE' },
  { value: 'pump_and_treat', label: 'Pump & Treat' },
  { value: 'air_sparging', label: 'Air Sparging' },
  { value: 'sve', label: 'SVE' },
  { value: 'isco', label: 'ISCO' },
  { value: 'bioremediation', label: 'Bioremediation' },
  { value: 'solar_skimmer', label: 'Solar Skimmer' },
  { value: 'mna', label: 'MNA' },
] as const;

const DEFAULT_DESIGN_FLOW_GPM: Record<string, number> = {
  mdpe: 7.5,
  pump_and_treat: 17.5,
  air_sparging: 5.0,
  sve: 10.0,
  isco: 0,
  bioremediation: 0,
  solar_skimmer: 1.0,
  mna: 0,
};

function fmtCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function fmtMass(lbs: number): string {
  if (lbs >= 1000) return `${(lbs / 1000).toFixed(1)}K lbs`;
  return `${lbs.toFixed(0)} lbs`;
}

function techLabel(tech: string): string {
  return TECHNOLOGIES.find(t => t.value === tech)?.label ?? tech;
}

interface Props {
  siteId: string;
  baselinePshLbs?: number;
  baselineDissolvedMg?: number;
}

export function RemediationImpactCalculator({ siteId, baselinePshLbs, baselineDissolvedMg }: Props) {
  const [systems, setSystems] = useState<RemediationSystemItem[]>([]);
  const [impact, setImpact] = useState<RemediationImpactResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Collapsed by default — same dropdown behavior as the MCL legend, saves
  // vertical space until the user wants to dig in.
  const [expanded, setExpanded] = useState(false);

  // Draft / what-if state — the form below the system list. Filling it in
  // recomputes impact preview without persisting; clicking Save persists.
  const [draftMode, setDraftMode] = useState(false);
  const [draftTech, setDraftTech] = useState<string>('mdpe');
  const [draftFlow, setDraftFlow] = useState<number>(DEFAULT_DESIGN_FLOW_GPM.mdpe);

  const loadSystems = useCallback(async () => {
    try {
      const resp = await fetchRemediationSystems(siteId);
      setSystems(resp.systems.filter(s => s.status === 'active'));
    } catch (err) {
      console.warn('[POLYGON] remediation systems fetch failed:', err);
    }
  }, [siteId]);

  const loadImpact = useCallback(async (drafts: RemediationDraftSystem[] = []) => {
    setLoading(true);
    try {
      const resp = await fetchRemediationImpact(siteId, {
        drafts,
        baselinePshLbs,
        baselineDissolvedMg,
      });
      setImpact(resp);
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to compute impact');
    } finally {
      setLoading(false);
    }
  }, [siteId, baselinePshLbs, baselineDissolvedMg]);

  useEffect(() => {
    loadSystems();
    loadImpact();
  }, [loadSystems, loadImpact]);

  // Recompute the impact preview whenever the draft form changes.
  const draftSystem: RemediationDraftSystem | null = useMemo(() => {
    if (!draftMode) return null;
    return {
      technology: draftTech,
      install_date: new Date().toISOString().slice(0, 10),
      design_flow_gpm: draftFlow,
    };
  }, [draftMode, draftTech, draftFlow]);

  useEffect(() => {
    loadImpact(draftSystem ? [draftSystem] : []);
  }, [draftSystem, loadImpact]);

  const handleAddSystem = async () => {
    if (!draftSystem) return;
    try {
      await createRemediationSystem(siteId, {
        technology: draftSystem.technology,
        status: 'active',
        install_date: draftSystem.install_date,
        extra_data: draftSystem.design_flow_gpm != null
          ? { design_flow_gpm_override: draftSystem.design_flow_gpm }
          : undefined,
      });
      setDraftMode(false);
      await loadSystems();
      await loadImpact();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to add system');
    }
  };

  const handleDelete = async (systemId: string) => {
    try {
      await deleteRemediationSystem(systemId);
      await loadSystems();
      await loadImpact();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to delete system');
    }
  };

  // Find the 5yr and 20yr horizon indices for the summary card
  const idx5yr = impact?.horizons_days.indexOf(1825) ?? -1;
  const idx20yr = impact?.horizons_days.indexOf(7300) ?? -1;
  const baselinePshAt20yr = impact && idx20yr >= 0 ? impact.baseline_psh_lbs[idx20yr] : 0;
  const adjustedPshAt20yr = impact && idx20yr >= 0 ? impact.adjusted_psh_lbs[idx20yr] : 0;
  const reductionPct = baselinePshAt20yr > 0
    ? ((baselinePshAt20yr - adjustedPshAt20yr) / baselinePshAt20yr) * 100
    : 0;

  // Find the draft's own per-system impact entry (system_id starts with "draft-")
  const draftImpact = impact?.per_system.find(s => s.is_draft);

  // SVG sparkline of baseline vs adjusted PSH across horizons
  const renderSparkline = (
    label: string,
    baseline: number[] | undefined,
    adjusted: number[] | undefined,
    labels: string[] | undefined,
  ) => {
    if (!baseline || !adjusted || baseline.length < 2) {
      return <div className="text-[9px] text-mono-600">{label}: no data</div>;
    }
    const W = 260;
    const H = 36;
    const max = Math.max(...baseline, 1);
    const ptsBase = baseline.map((v, i) => ({
      x: (i / (baseline.length - 1)) * W,
      y: H - (v / max) * (H - 4) - 2,
    }));
    const ptsAdj = adjusted.map((v, i) => ({
      x: (i / (adjusted.length - 1)) * W,
      y: H - (v / max) * (H - 4) - 2,
    }));
    return (
      <div className="mt-1">
        <div className="flex items-center justify-between text-[9px] text-mono-500">
          <span className="font-semibold">{label}</span>
          <span>
            <span className="text-mono-600">baseline</span> · <span className="text-emerald-400">adjusted</span>
          </span>
        </div>
        <svg width={W} height={H}>
          <polyline
            points={ptsBase.map(p => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke="#666"
            strokeWidth={1.2}
            strokeDasharray="2 2"
          />
          <polyline
            points={ptsAdj.map(p => `${p.x},${p.y}`).join(' ')}
            fill="none"
            stroke="#10b981"
            strokeWidth={1.6}
          />
          {ptsAdj.map((p, i) => (
            <circle key={i} cx={p.x} cy={p.y} r={1.5} fill="#10b981" />
          ))}
        </svg>
        <div className="flex justify-between text-[8px] text-mono-600">
          {(labels ?? []).map((lbl, i) => <span key={i}>{lbl}</span>)}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-mono-900 border border-mono-800 rounded-lg">
      {/* Header — click to expand/collapse, just like the MCL legend */}
      <button
        type="button"
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between p-3 hover:bg-mono-850 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-2">
          <div className="text-xs text-mono-300 uppercase tracking-wider font-semibold font-orbitron">
            Remediation Impact
          </div>
          {systems.length > 0 && (
            <span className="text-[9px] text-mono-500">
              {systems.length} active · {reductionPct > 0 ? `${reductionPct.toFixed(0)}% PSH reduction @ 20yr` : 'no reduction yet'}
            </span>
          )}
          {systems.length === 0 && (
            <span className="text-[9px] text-mono-600 italic">no systems declared</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {expanded && (
            <span
              onClick={(e) => { e.stopPropagation(); setDraftMode(v => !v); }}
              className="flex items-center gap-1 text-[10px] text-mono-400 hover:text-mono-200 px-2 py-0.5 rounded border border-mono-700 cursor-pointer"
            >
              {draftMode ? <XMarkIcon className="h-3 w-3" /> : <PlusIcon className="h-3 w-3" />}
              {draftMode ? 'Cancel' : 'Add Asset'}
            </span>
          )}
          {expanded ? <ChevronUpIcon className="h-3.5 w-3.5 text-mono-500" /> : <ChevronDownIcon className="h-3.5 w-3.5 text-mono-500" />}
        </div>
      </button>

      {/* Body — only renders when expanded, mirroring the legend's collapse */}
      {expanded && (
      <div className="px-3 pb-3 space-y-3 border-t border-mono-800 pt-3">
      {error && (
        <div className="text-[10px] text-red-300 bg-red-900/30 border border-red-800/40 rounded px-2 py-1">
          {error}
        </div>
      )}

      {/* Active systems list */}
      {systems.length === 0 && !draftMode && (
        <div className="text-[10px] text-mono-600 italic">No active remediation systems declared.</div>
      )}
      {systems.length > 0 && (
        <div className="space-y-1.5">
          {systems.map(sys => {
            const sysImpact = impact?.per_system.find(p => p.system_id === sys.id && !p.is_draft);
            const pshAt20yr = sysImpact && idx20yr >= 0
              ? sysImpact.removed_psh_lbs_by_horizon[String(impact!.horizons_days[idx20yr])] ?? 0
              : 0;
            return (
              <div key={sys.id} className="bg-mono-850 rounded px-2 py-1.5 flex items-start justify-between">
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-mono-100">{techLabel(sys.technology)}</div>
                  <div className="text-[9px] text-mono-500">
                    {sysImpact ? `ROI ${sysImpact.roi_ft.toFixed(0)}ft · ${sysImpact.wells_in_roi} wells captured` : 'computing…'}
                    {sys.install_date ? ` · since ${sys.install_date}` : ''}
                  </div>
                  {sysImpact && (
                    <div className="text-[9px] text-emerald-400 mt-0.5">
                      Removes ~{fmtMass(pshAt20yr)} PSH by 20yr
                    </div>
                  )}
                </div>
                <button
                  onClick={() => handleDelete(sys.id)}
                  className="text-mono-500 hover:text-red-400 p-1"
                  title="Remove system"
                >
                  <TrashIcon className="h-3 w-3" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Draft what-if form */}
      {draftMode && (
        <div className="bg-mono-850 border border-cyan-700/40 rounded p-2 space-y-2">
          <div className="text-[10px] text-cyan-400 uppercase tracking-wider">What-if Preview</div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[9px] text-mono-500 flex flex-col gap-0.5">
              Technology
              <select
                value={draftTech}
                onChange={e => {
                  setDraftTech(e.target.value);
                  setDraftFlow(DEFAULT_DESIGN_FLOW_GPM[e.target.value] ?? 0);
                }}
                className="bg-mono-900 border border-mono-700 rounded px-1 py-0.5 text-xs text-mono-100"
              >
                {TECHNOLOGIES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </label>
            <label className="text-[9px] text-mono-500 flex flex-col gap-0.5">
              Design flow (gpm)
              <input
                type="number"
                step="0.5"
                value={draftFlow}
                onChange={e => setDraftFlow(parseFloat(e.target.value) || 0)}
                className="bg-mono-900 border border-mono-700 rounded px-1 py-0.5 text-xs text-mono-100"
              />
            </label>
          </div>
          {draftImpact && (
            <div className="text-[10px] text-mono-300 bg-mono-900 rounded p-1.5 border border-mono-700">
              <div>ROI ~{draftImpact.roi_ft.toFixed(0)}ft · {draftImpact.wells_in_roi} wells in capture zone</div>
              <div className="text-emerald-400 mt-0.5">
                +{fmtMass(
                  idx20yr >= 0
                    ? draftImpact.removed_psh_lbs_by_horizon[String(impact!.horizons_days[idx20yr])] ?? 0
                    : 0
                )} PSH removed by 20yr
              </div>
            </div>
          )}
          <button
            onClick={handleAddSystem}
            className="w-full px-2 py-1 text-[10px] tracking-wider bg-cyan-600/30 hover:bg-cyan-600/50 text-cyan-200 border border-cyan-600/50 rounded"
          >
            ADD TO SITE
          </button>
        </div>
      )}

      {/* Cost Savings — "what remediation buys you" at 20yr horizon.
          Compares v3.2 cost curve with vs without declared systems. */}
      {impact?.cost_savings && systems.length > 0 && (
        <div className="border-t border-mono-800 pt-2">
          <div className="bg-emerald-900/20 border border-emerald-700/30 rounded p-2">
            <div className="text-[10px] text-emerald-400 uppercase tracking-wider font-semibold">
              Cost Savings @ 20yr
            </div>
            <div className="text-lg font-bold text-emerald-300 font-orbitron mt-0.5">
              {fmtCurrency(impact.cost_savings.savings_20yr)}
            </div>
            <div className="text-[9px] text-mono-400 mt-1 space-y-0.5">
              <div className="flex justify-between">
                <span>Without systems:</span>
                <span className="font-mono text-mono-300">{fmtCurrency(impact.cost_savings.liability_20yr_without)}</span>
              </div>
              <div className="flex justify-between">
                <span>With systems:</span>
                <span className="font-mono text-emerald-300">{fmtCurrency(impact.cost_savings.liability_20yr_with)}</span>
              </div>
            </div>
            {impact.cost_savings.savings_start_now > 0 && (
              <div className="text-[9px] text-emerald-400 mt-1">
                Start-now savings: {fmtCurrency(impact.cost_savings.savings_start_now)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Site-wide impact summary */}
      {impact && systems.length > 0 && (
        <div className="border-t border-mono-800 pt-2 space-y-2">
          <div className="grid grid-cols-2 gap-2 text-[10px]">
            <div>
              <div className="text-mono-500 uppercase tracking-wider">Baseline @ 20yr</div>
              <div className="text-mono-300 font-mono">{fmtMass(baselinePshAt20yr)} PSH</div>
            </div>
            <div>
              <div className="text-emerald-400 uppercase tracking-wider">With Systems</div>
              <div className="text-emerald-300 font-mono">
                {fmtMass(adjustedPshAt20yr)} PSH ({reductionPct.toFixed(0)}% ↓)
              </div>
            </div>
          </div>
          {renderSparkline(
            'PSH trajectory',
            impact.baseline_psh_lbs,
            impact.adjusted_psh_lbs,
            impact.horizons_labels,
          )}
          {renderSparkline(
            'Dissolved trajectory',
            impact.baseline_dissolved_mg,
            impact.adjusted_dissolved_mg,
            impact.horizons_labels,
          )}
        </div>
      )}

      {loading && (
        <div className="text-[9px] text-mono-600 italic">Computing impact…</div>
      )}
      </div>
      )}
    </div>
  );
}
